/**
 * Validates that a parameter is present, non-empty, and meaningful
 * @param param - The parameter to validate
 * @param minLength - Minimum length requirement (default: 1)
 * @returns true if parameter is valid, false otherwise
 */
export const isValidParameter = (
  param: string | undefined | null,
  minLength: number = 1,
): boolean => {
  if (!param || typeof param !== 'string') return false;
  const trimmed = param.trim();
  if (trimmed === '' || trimmed === 'undefined' || trimmed === 'null') return false;
  return trimmed.length >= minLength;
};

/**
 * Validates eventId parameter
 * EventIds should be numeric strings
 */
export const isValidEventId = (eventId: string | undefined | null): boolean => {
  return isValidParameter(eventId);
};

/**
 * Validates organizationId parameter
 * OrganizationIds should be numeric strings
 */
export const isValidOrganizationId = (organizationId: string | undefined | null): boolean => {
  return isValidParameter(organizationId);
};

/**
 * Validates userId parameter
 * UserIds should be numeric strings
 */
export const isValidUserId = (userId: string | undefined | null): boolean => {
  return isValidParameter(userId);
};

/**
 * Validates multiple parameters at once
 * @param params - Object containing parameters to validate
 * @returns true if all provided parameters are valid
 */
export const areValidParameters = (params: {
  eventId?: string | undefined | null;
  organizationId?: string | undefined | null;
  userId?: string | undefined | null;
}): boolean => {
  const { eventId, organizationId, userId } = params;

  // Only validate parameters that are provided
  const validations: boolean[] = [];

  if (eventId !== undefined) {
    validations.push(isValidEventId(eventId));
  }

  if (organizationId !== undefined) {
    validations.push(isValidOrganizationId(organizationId));
  }

  if (userId !== undefined) {
    validations.push(isValidUserId(userId));
  }

  // All provided parameters must be valid
  return validations.length > 0 && validations.every(Boolean);
};
